'use client';
import React, { useState } from 'react';
import { Shield, AlertTriangle, Clock, Bell, Save, RotateCcw, CheckCircle, ChevronDown, Info, UserCheck, Activity, Flag } from 'lucide-react';



type ConfigSection = 'aml' | 'kyc' | 'risk' | 'alerts';

interface AMLThreshold {
  id: string;
  name: string;
  description: string;
  value: number;
  unit: string;
  min: number;
  max: number;
  step: number;
  severity: 'critical' | 'warning' | 'info';
  enabled: boolean;
}

interface KYCSla {
  id: string;
  name: string;
  description: string;
  standardDays: number;
  eddDays: number;
  escalationDays: number;
  enabled: boolean;
}

interface RiskTrigger {
  id: string;
  name: string;
  description: string;
  scoreThreshold: number;
  action: 'flag' | 'block' | 'edd' | 'notify';
  enabled: boolean;
}

interface AlertRule {
  id: string;
  name: string;
  condition: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  channels: ('email' | 'dashboard' | 'sms')[];
  cooldownMinutes: number;
  enabled: boolean;
}

const initialAMLThresholds: AMLThreshold[] = [
  { id: 'aml-1', name: 'Single Transaction Alert', description: 'Flag individual transactions above this amount for AML review', value: 10000, unit: 'USD', min: 1000, max: 100000, step: 1000, severity: 'critical', enabled: true },
  { id: 'aml-2', name: 'Aggregate 30-Day Threshold', description: 'Cumulative transaction volume per investor over 30 days', value: 50000, unit: 'USD', min: 10000, max: 500000, step: 5000, severity: 'warning', enabled: true },
  { id: 'aml-3', name: 'Structuring Detection Window', description: 'Number of transactions below reporting threshold within window', value: 5, unit: 'transactions', min: 2, max: 20, step: 1, severity: 'critical', enabled: true },
  { id: 'aml-4', name: 'Velocity Check Interval', description: 'Time window (hours) for transaction velocity monitoring', value: 24, unit: 'hours', min: 1, max: 168, step: 1, severity: 'warning', enabled: true },
  { id: 'aml-5', name: 'High-Risk Jurisdiction Multiplier', description: 'Reduce thresholds by this factor for high-risk jurisdictions', value: 50, unit: '%', min: 10, max: 90, step: 5, severity: 'critical', enabled: false },
  { id: 'aml-6', name: 'PEP Transaction Limit', description: 'Maximum single transaction for Politically Exposed Persons', value: 5000, unit: 'USD', min: 500, max: 50000, step: 500, severity: 'critical', enabled: true },
];

const initialKYCSlas: KYCSla[] = [
  { id: 'kyc-1', name: 'Individual KYC Turnaround', description: 'Standard review SLA for individual investor KYC submissions', standardDays: 3, eddDays: 10, escalationDays: 7, enabled: true },
  { id: 'kyc-2', name: 'Corporate KYB Turnaround', description: 'Standard review SLA for corporate entity KYB submissions', standardDays: 7, eddDays: 21, escalationDays: 14, enabled: true },
  { id: 'kyc-3', name: 'Institutional KYC Turnaround', description: 'Review SLA for institutional investor onboarding', standardDays: 5, eddDays: 15, escalationDays: 10, enabled: true },
  { id: 'kyc-4', name: 'Periodic Review Cycle', description: 'Frequency of scheduled KYC refresh for existing investors', standardDays: 365, eddDays: 180, escalationDays: 30, enabled: true },
  { id: 'kyc-5', name: 'EDD Completion SLA', description: 'Maximum days to complete Enhanced Due Diligence', standardDays: 14, eddDays: 30, escalationDays: 21, enabled: true },
];

const initialRiskTriggers: RiskTrigger[] = [
  { id: 'risk-1', name: 'Low Risk Threshold', description: 'Scores below this value are classified as low risk', scoreThreshold: 30, action: 'notify', enabled: true },
  { id: 'risk-2', name: 'Medium Risk Threshold', description: 'Scores above this trigger medium risk classification', scoreThreshold: 50, action: 'flag', enabled: true },
  { id: 'risk-3', name: 'High Risk Threshold', description: 'Scores above this trigger EDD requirement', scoreThreshold: 70, action: 'edd', enabled: true },
  { id: 'risk-4', name: 'Critical Risk Block', description: 'Scores above this automatically block transactions', scoreThreshold: 90, action: 'block', enabled: true },
  { id: 'risk-5', name: 'PEP Score Boost', description: 'Automatic score addition for PEP-matched investors', scoreThreshold: 20, action: 'flag', enabled: true },
  { id: 'risk-6', name: 'Sanctions Match Override', description: 'Score override for confirmed sanctions matches', scoreThreshold: 95, action: 'block', enabled: true },
];

const initialAlertRules: AlertRule[] = [
  { id: 'alert-1', name: 'Critical AML Flag', condition: 'AML flag severity = Critical', severity: 'critical', channels: ['email', 'dashboard', 'sms'], cooldownMinutes: 0, enabled: true },
  { id: 'alert-2', name: 'KYC SLA Breach', condition: 'KYC review exceeds standard SLA', severity: 'high', channels: ['email', 'dashboard'], cooldownMinutes: 60, enabled: true },
  { id: 'alert-3', name: 'Sanctions Match Detected', condition: 'Investor matches sanctions list', severity: 'critical', channels: ['email', 'dashboard', 'sms'], cooldownMinutes: 0, enabled: true },
  { id: 'alert-4', name: 'High Risk Score Assigned', condition: 'Risk score crosses high threshold', severity: 'high', channels: ['email', 'dashboard'], cooldownMinutes: 30, enabled: true },
  { id: 'alert-5', name: 'Structuring Pattern Detected', condition: 'Transaction velocity triggers structuring rule', severity: 'critical', channels: ['email', 'dashboard'], cooldownMinutes: 0, enabled: true },
  { id: 'alert-6', name: 'EDD Overdue', condition: 'EDD not completed within SLA', severity: 'medium', channels: ['dashboard'], cooldownMinutes: 1440, enabled: true },
  { id: 'alert-7', name: 'PEP Match Detected', condition: 'Investor matches PEP database', severity: 'high', channels: ['email', 'dashboard'], cooldownMinutes: 0, enabled: true },
];

const severityColors: Record<string, string> = {
  critical: 'bg-critical/10 text-critical border-critical/20',
  high: 'bg-critical/10 text-critical border-critical/20',
  warning: 'bg-warning/10 text-warning border-warning/20',
  medium: 'bg-warning/10 text-warning border-warning/20',
  info: 'bg-info/10 text-info border-info/20',
  low: 'bg-surface-4 text-muted-foreground border-border',
};

const actionColors: Record<string, string> = {
  flag: 'bg-warning/10 text-warning border-warning/20',
  block: 'bg-critical/10 text-critical border-critical/20',
  edd: 'bg-accent/10 text-accent border-accent/20',
  notify: 'bg-info/10 text-info border-info/20',
};

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 ${checked ? 'bg-positive' : 'bg-surface-4'}`}
    >
      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-4.5' : 'translate-x-0.5'}`} />
    </button>
  );
}

function SaveBanner({ onSave, onReset }: { onSave: () => void; onReset: () => void }) {
  return (
    <div className="sticky top-0 z-20 bg-primary/5 border border-primary/20 rounded-xl px-4 py-3 flex items-center justify-between gap-3">
      <div className="flex items-center gap-2">
        <Info size={14} className="text-primary flex-shrink-0" />
        <p className="text-sm text-foreground font-medium">You have unsaved configuration changes.</p>
      </div>
      <div className="flex items-center gap-2">
        <button onClick={onReset} className="btn-secondary text-xs gap-1.5"><RotateCcw size={11} /> Reset</button>
        <button onClick={onSave} className="btn-primary text-xs gap-1.5"><Save size={11} /> Save Changes</button>
      </div>
    </div>
  );
}

export default function ComplianceConfigDashboard() {
  const [activeSection, setActiveSection] = useState<ConfigSection>('aml');
  const [amlThresholds, setAmlThresholds] = useState<AMLThreshold[]>(initialAMLThresholds);
  const [kycSlas, setKycSlas] = useState<KYCSla[]>(initialKYCSlas);
  const [riskTriggers, setRiskTriggers] = useState<RiskTrigger[]>(initialRiskTriggers);
  const [alertRules, setAlertRules] = useState<AlertRule[]>(initialAlertRules);
  const [isDirty, setIsDirty] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  const markDirty = () => setIsDirty(true);

  const handleSave = () => {
    setIsDirty(false);
    setSavedAt('2026-08-21 01:19');
  };

  const handleReset = () => {
    setAmlThresholds(initialAMLThresholds);
    setKycSlas(initialKYCSlas);
    setRiskTriggers(initialRiskTriggers);
    setAlertRules(initialAlertRules);
    setIsDirty(false);
  };

  const updateAML = (id: string, field: keyof AMLThreshold, value: unknown) => {
    setAmlThresholds(prev => prev.map(t => t.id === id ? { ...t, [field]: value } : t));
    markDirty();
  };

  const updateKYC = (id: string, field: keyof KYCSla, value: unknown) => {
    setKycSlas(prev => prev.map(s => s.id === id ? { ...s, [field]: value } : s));
    markDirty();
  };

  const updateRisk = (id: string, field: keyof RiskTrigger, value: unknown) => {
    setRiskTriggers(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r));
    markDirty();
  };

  const updateAlert = (id: string, field: keyof AlertRule, value: unknown) => {
    setAlertRules(prev => prev.map(a => a.id === id ? { ...a, [field]: value } : a));
    markDirty();
  };

  const toggleAlertChannel = (id: string, channel: 'email' | 'dashboard' | 'sms') => {
    setAlertRules(prev => prev.map(a => {
      if (a.id !== id) return a;
      const has = a.channels.includes(channel);
      const updated = has ? a.channels.filter(c => c !== channel) : [...a.channels, channel];
      return { ...a, channels: updated.length > 0 ? updated : a.channels };
    }));
    markDirty();
  };

  const sections: { key: ConfigSection; label: string; icon: React.ElementType; count: number }[] = [
    { key: 'aml', label: 'AML Thresholds', icon: Shield, count: amlThresholds.filter(t => t.enabled).length },
    { key: 'kyc', label: 'KYC SLAs', icon: UserCheck, count: kycSlas.filter(s => s.enabled).length },
    { key: 'risk', label: 'Risk Score Triggers', icon: Activity, count: riskTriggers.filter(r => r.enabled).length },
    { key: 'alerts', label: 'Alert Severity Rules', icon: Bell, count: alertRules.filter(a => a.enabled).length },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-foreground tracking-tight">Compliance Configuration</h1>
          <p className="text-sm text-muted-foreground mt-1">
            AML thresholds · KYC SLAs · Risk triggers · Alert rules · No code changes required
            {savedAt && <span className="ml-2 text-positive"><CheckCircle size={11} className="inline mr-1" />Saved {savedAt}</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleReset} className="btn-secondary text-xs gap-1.5"><RotateCcw size={12} /> Reset All</button>
          <button onClick={handleSave} className={`btn-primary text-xs gap-1.5 ${!isDirty ? 'opacity-50 cursor-not-allowed' : ''}`} disabled={!isDirty}>
            <Save size={12} /> Save Changes
          </button>
        </div>
      </div>

      {/* Unsaved changes banner */}
      {isDirty && <SaveBanner onSave={handleSave} onReset={handleReset} />}

      {/* Section tabs */}
      <div className="flex gap-0 border-b border-border overflow-x-auto">
        {sections.map(({ key, label, icon: SectionIcon, count }) => (
          <button
            key={key}
            onClick={() => setActiveSection(key)}
            className={`portal-tab flex items-center gap-1.5 ${activeSection === key ? 'active' : ''}`}
          >
            <SectionIcon size={13} />{label}
            <span className={`text-xs font-mono-data px-1.5 py-0.5 rounded-full ${activeSection === key ? 'bg-primary/20 text-primary' : 'bg-surface-4 text-muted-foreground'}`}>{count}</span>
          </button>
        ))}
      </div>

      {/* AML Thresholds */}
      {activeSection === 'aml' && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 p-3 bg-warning/5 border border-warning/20 rounded-xl">
            <AlertTriangle size={14} className="text-warning flex-shrink-0" />
            <p className="text-xs text-foreground">Changes to AML thresholds take effect immediately for new transactions. Existing flagged items are not retroactively re-evaluated.</p>
          </div>
          <div className="space-y-3">
            {amlThresholds.map(threshold => (
              <div key={threshold.id} className={`glass-card rounded-xl p-4 transition-opacity ${!threshold.enabled ? 'opacity-60' : ''}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-semibold text-sm text-foreground">{threshold.name}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold border ${severityColors[threshold.severity]}`}>
                        {threshold.severity}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mb-3">{threshold.description}</p>
                    <div className="flex items-center gap-4">
                      <div className="flex-1 max-w-xs">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-muted-foreground">Value</span>
                          <span className="text-sm font-bold font-mono-data text-foreground">
                            {threshold.unit === 'USD' ? '$' : ''}{threshold.value.toLocaleString()} {threshold.unit !== 'USD' ? threshold.unit : ''}
                          </span>
                        </div>
                        <input
                          type="range"
                          min={threshold.min}
                          max={threshold.max}
                          step={threshold.step}
                          value={threshold.value}
                          disabled={!threshold.enabled}
                          onChange={e => updateAML(threshold.id, 'value', Number(e.target.value))}
                          className="w-full h-1.5 rounded-full appearance-none cursor-pointer accent-primary"
                        />
                        <div className="flex justify-between text-xs text-muted-foreground mt-0.5">
                          <span>{threshold.unit === 'USD' ? '$' : ''}{threshold.min.toLocaleString()}</span>
                          <span>{threshold.unit === 'USD' ? '$' : ''}{threshold.max.toLocaleString()}</span>
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        <label className="block text-xs text-muted-foreground mb-1">Severity</label>
                        <div className="relative">
                          <select
                            value={threshold.severity}
                            disabled={!threshold.enabled}
                            onChange={e => updateAML(threshold.id, 'severity', e.target.value)}
                            className="text-xs bg-surface-3 border border-border rounded-lg px-2.5 py-1.5 text-foreground appearance-none pr-6 focus:outline-none focus:ring-1 focus:ring-primary/40"
                          >
                            <option value="critical">Critical</option>
                            <option value="warning">Warning</option>
                            <option value="info">Info</option>
                          </select>
                          <ChevronDown size={11} className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                        </div>
                      </div>
                    </div>
                  </div>
                  <Toggle checked={threshold.enabled} onChange={v => updateAML(threshold.id, 'enabled', v)} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* KYC SLAs */}
      {activeSection === 'kyc' && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 p-3 bg-info/5 border border-info/20 rounded-xl">
            <Clock size={14} className="text-info flex-shrink-0" />
            <p className="text-xs text-foreground">SLA changes apply to new submissions only. In-progress reviews retain their original SLA deadlines.</p>
          </div>
          <div className="space-y-3">
            {kycSlas.map(sla => (
              <div key={sla.id} className={`glass-card rounded-xl p-4 transition-opacity ${!sla.enabled ? 'opacity-60' : ''}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-foreground mb-0.5">{sla.name}</p>
                    <p className="text-xs text-muted-foreground mb-3">{sla.description}</p>
                    <div className="grid grid-cols-3 gap-3">
                      {[
                        { label: 'Standard SLA', field: 'standardDays' as keyof KYCSla, value: sla.standardDays, color: 'text-positive' },
                        { label: 'EDD SLA', field: 'eddDays' as keyof KYCSla, value: sla.eddDays, color: 'text-warning' },
                        { label: 'Escalation After', field: 'escalationDays' as keyof KYCSla, value: sla.escalationDays, color: 'text-critical' },
                      ].map(({ label, field, value, color }) => (
                        <div key={field}>
                          <label className="block text-xs text-muted-foreground mb-1">{label}</label>
                          <div className="flex items-center gap-1.5">
                            <input
                              type="number"
                              min={1}
                              max={365}
                              value={value}
                              disabled={!sla.enabled}
                              onChange={e => updateKYC(sla.id, field, Number(e.target.value))}
                              className="w-16 px-2 py-1.5 text-sm font-mono-data font-bold bg-surface-3 border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary/40 text-foreground text-center"
                            />
                            <span className={`text-xs font-medium ${color}`}>days</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <Toggle checked={sla.enabled} onChange={v => updateKYC(sla.id, 'enabled', v)} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Risk Score Triggers */}
      {activeSection === 'risk' && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 p-3 bg-critical/5 border border-critical/20 rounded-xl">
            <Flag size={14} className="text-critical flex-shrink-0" />
            <p className="text-xs text-foreground">Risk score thresholds are evaluated in ascending order. The highest matching threshold determines the action taken.</p>
          </div>

          {/* Visual score band */}
          <div className="glass-card rounded-xl p-4">
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">Risk Score Band Overview</p>
            <div className="relative h-6 rounded-full overflow-hidden flex">
              <div className="bg-positive/60 flex items-center justify-center text-xs font-bold text-white" style={{ width: `${riskTriggers[0]?.scoreThreshold ?? 30}%` }}>
                Low
              </div>
              <div className="bg-warning/60 flex items-center justify-center text-xs font-bold text-white" style={{ width: `${(riskTriggers[1]?.scoreThreshold ?? 50) - (riskTriggers[0]?.scoreThreshold ?? 30)}%` }}>
                Med
              </div>
              <div className="bg-orange-500/60 flex items-center justify-center text-xs font-bold text-white" style={{ width: `${(riskTriggers[2]?.scoreThreshold ?? 70) - (riskTriggers[1]?.scoreThreshold ?? 50)}%` }}>
                High
              </div>
              <div className="bg-critical/70 flex-1 flex items-center justify-center text-xs font-bold text-white">
                Critical
              </div>
            </div>
            <div className="flex justify-between text-xs text-muted-foreground mt-1">
              <span>0</span>
              <span>{riskTriggers[0]?.scoreThreshold ?? 30}</span>
              <span>{riskTriggers[1]?.scoreThreshold ?? 50}</span>
              <span>{riskTriggers[2]?.scoreThreshold ?? 70}</span>
              <span>100</span>
            </div>
          </div>

          <div className="space-y-3">
            {riskTriggers.map(trigger => (
              <div key={trigger.id} className={`glass-card rounded-xl p-4 transition-opacity ${!trigger.enabled ? 'opacity-60' : ''}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <span className="font-semibold text-sm text-foreground">{trigger.name}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold border ${actionColors[trigger.action]}`}>
                        {trigger.action.toUpperCase()}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mb-3">{trigger.description}</p>
                    <div className="flex items-center gap-4 flex-wrap">
                      <div className="flex items-center gap-2">
                        <label className="text-xs text-muted-foreground">Score Threshold</label>
                        <input
                          type="number"
                          min={0}
                          max={100}
                          value={trigger.scoreThreshold}
                          disabled={!trigger.enabled}
                          onChange={e => updateRisk(trigger.id, 'scoreThreshold', Number(e.target.value))}
                          className="w-16 px-2 py-1.5 text-sm font-mono-data font-bold bg-surface-3 border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary/40 text-foreground text-center"
                        />
                        <span className="text-xs text-muted-foreground">/ 100</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="text-xs text-muted-foreground">Action</label>
                        <div className="relative">
                          <select
                            value={trigger.action}
                            disabled={!trigger.enabled}
                            onChange={e => updateRisk(trigger.id, 'action', e.target.value)}
                            className="text-xs bg-surface-3 border border-border rounded-lg px-2.5 py-1.5 text-foreground appearance-none pr-6 focus:outline-none focus:ring-1 focus:ring-primary/40"
                          >
                            <option value="notify">Notify</option>
                            <option value="flag">Flag</option>
                            <option value="edd">Require EDD</option>
                            <option value="block">Block</option>
                          </select>
                          <ChevronDown size={11} className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                        </div>
                      </div>
                    </div>
                  </div>
                  <Toggle checked={trigger.enabled} onChange={v => updateRisk(trigger.id, 'enabled', v)} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Alert Severity Rules */}
      {activeSection === 'alerts' && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 p-3 bg-primary/5 border border-primary/20 rounded-xl">
            <Bell size={14} className="text-primary flex-shrink-0" />
            <p className="text-xs text-foreground">Alert rules control how compliance events are surfaced to officers. Cooldown prevents duplicate notifications within the specified window.</p>
          </div>
          <div className="space-y-3">
            {alertRules.map(rule => (
              <div key={rule.id} className={`glass-card rounded-xl p-4 transition-opacity ${!rule.enabled ? 'opacity-60' : ''}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <span className="font-semibold text-sm text-foreground">{rule.name}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold border ${severityColors[rule.severity]}`}>
                        {rule.severity}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mb-3">{rule.condition}</p>
                    <div className="flex items-center gap-4 flex-wrap">
                      {/* Severity */}
                      <div className="flex items-center gap-2">
                        <label className="text-xs text-muted-foreground">Severity</label>
                        <div className="relative">
                          <select
                            value={rule.severity}
                            disabled={!rule.enabled}
                            onChange={e => updateAlert(rule.id, 'severity', e.target.value)}
                            className="text-xs bg-surface-3 border border-border rounded-lg px-2.5 py-1.5 text-foreground appearance-none pr-6 focus:outline-none focus:ring-1 focus:ring-primary/40"
                          >
                            <option value="critical">Critical</option>
                            <option value="high">High</option>
                            <option value="medium">Medium</option>
                            <option value="low">Low</option>
                          </select>
                          <ChevronDown size={11} className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                        </div>
                      </div>
                      {/* Channels */}
                      <div className="flex items-center gap-2">
                        <label className="text-xs text-muted-foreground">Channels</label>
                        <div className="flex gap-1.5">
                          {(['email', 'dashboard', 'sms'] as const).map(ch => (
                            <button
                              key={ch}
                              disabled={!rule.enabled}
                              onClick={() => toggleAlertChannel(rule.id, ch)}
                              className={`text-xs px-2 py-1 rounded-lg border font-semibold capitalize transition-all ${
                                rule.channels.includes(ch)
                                  ? 'bg-primary/10 border-primary/30 text-primary' :'bg-surface-3 border-border text-muted-foreground'
                              }`}
                            >
                              {ch}
                            </button>
                          ))}
                        </div>
                      </div>
                      {/* Cooldown */}
                      <div className="flex items-center gap-2">
                        <label className="text-xs text-muted-foreground">Cooldown</label>
                        <input
                          type="number"
                          min={0}
                          max={10080}
                          step={15}
                          value={rule.cooldownMinutes}
                          disabled={!rule.enabled}
                          onChange={e => updateAlert(rule.id, 'cooldownMinutes', Number(e.target.value))}
                          className="w-16 px-2 py-1.5 text-sm font-mono-data bg-surface-3 border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary/40 text-foreground text-center"
                        />
                        <span className="text-xs text-muted-foreground">min</span>
                      </div>
                    </div>
                  </div>
                  <Toggle checked={rule.enabled} onChange={v => updateAlert(rule.id, 'enabled', v)} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
